/// <reference types="google.maps" />

import { useEffect, useRef, useState } from 'react'
import { importLibrary, setOptions } from '@googlemaps/js-api-loader'

export interface MapPoint {
  lat: number
  lng: number
}

export interface RouteMetrics {
  distanceMeters: number
  durationSeconds: number
  updatedAt: number
}

interface Props {
  pickup?: MapPoint | null
  dropoff?: MapPoint | null
  stops?: (MapPoint & { stopId?: number; visitedAt?: string | null })[]
  driverPosition?: MapPoint | null
  routeFrom?: MapPoint | null
  routeTo?: MapPoint | null
  // A generic "currently being picked" marker, for the location-picker screen —
  // separate from pickup/dropoff since it represents a point not yet committed.
  selection?: { point: MapPoint; color: string; glyph: string } | null
  // Past driver positions for this trip — rendered as a fading trail behind
  // the live marker (Uber/Ola-style "path already travelled").
  trail?: MapPoint[]
  // Online-but-unassigned drivers near the pickup point — shown on the
  // booking/finding-driver screens (before anyone is actually matched) so
  // the map isn't empty while choosing a vehicle, Rapido/Uber-style.
  nearbyVehicles?: { driverId: number; lat: number; lng: number; vehicleIcon: string }[]
  // The live driver-matching search boundary (see SEARCH_RADIUS_TIERS on the
  // backend) — draws a boundary ring at the current radius plus continuous
  // outward sonar pulses while actively searching. `exhausted` switches it
  // to a muted "nothing found in this area" style once the widest tier has
  // also timed out.
  searchRadius?: { center: MapPoint; radiusKm: number; exhausted?: boolean } | null
  onPick?: (point: MapPoint) => void
  onRouteMetrics?: (metrics: RouteMetrics | null) => void
  className?: string
}

/** Compass bearing (0=N, 90=E, ...) from one point to another, in degrees. */
function computeBearing(from: MapPoint, to: MapPoint): number {
  const toRad = (d: number) => (d * Math.PI) / 180
  const toDeg = (r: number) => (r * 180) / Math.PI
  const dLng = toRad(to.lng - from.lng)
  const lat1 = toRad(from.lat)
  const lat2 = toRad(to.lat)
  const y = Math.sin(dLng) * Math.cos(lat2)
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng)
  return (toDeg(Math.atan2(y, x)) + 360) % 360
}

// Rough meters-per-degree-latitude, good enough to filter out GPS jitter —
// without this, tiny noise between near-identical fixes while the driver is
// stopped would make the arrow spin randomly like a compass next to a magnet.
const MIN_MOVE_METERS = 5
function movedEnough(from: MapPoint, to: MapPoint): boolean {
  const dLat = (to.lat - from.lat) * 111_000
  const dLng = (to.lng - from.lng) * 111_000 * Math.cos((from.lat * Math.PI) / 180)
  return Math.sqrt(dLat * dLat + dLng * dLng) >= MIN_MOVE_METERS
}

function haversineMeters(from: MapPoint, to: MapPoint) {
  const R = 6371000
  const lat1 = from.lat * Math.PI / 180
  const lat2 = to.lat * Math.PI / 180
  const dLat = (to.lat - from.lat) * Math.PI / 180
  const dLng = (to.lng - from.lng) * Math.PI / 180
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

/**
 * Tracks the driver's direction of travel from consecutive raw GPS fixes
 * (the backend doesn't record device heading — see useLocationReporter).
 * Holds the last known heading rather than resetting it whenever the driver
 * is briefly stationary, exactly like Google/Uber's live-location arrow.
 */
function useHeading(target: MapPoint | null) {
  const [heading, setHeading] = useState(0)
  const lastRef = useRef<MapPoint | null>(null)

  useEffect(() => {
    if (!target) { lastRef.current = null; return }
    const last = lastRef.current
    if (last && movedEnough(last, target)) {
      setHeading(computeBearing(last, target))
    }
    lastRef.current = target
  }, [target?.lat, target?.lng])

  return heading
}

/**
 * Tweens the driver marker smoothly between successive positions instead of
 * snapping on every poll/ping — the single biggest visual gap vs a real
 * Uber/Ola map, where the car glides continuously rather than teleporting
 * every few seconds.
 */
function useAnimatedPosition(target: MapPoint | null, durationMs = 3500) {
  const [pos, setPos] = useState<MapPoint | null>(target)
  const posRef = useRef<MapPoint | null>(target)
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    if (!target) {
      posRef.current = null
      setPos(null)
      return
    }
    const from = posRef.current ?? target
    if (from.lat === target.lat && from.lng === target.lng) return
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    const start = performance.now()
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs)
      const eased = 1 - Math.pow(1 - t, 3)
      const next = { lat: from.lat + (target.lat - from.lat) * eased, lng: from.lng + (target.lng - from.lng) * eased }
      posRef.current = next
      setPos(next)
      if (t < 1) rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.lat, target?.lng])

  return pos
}

export default function RealMap({ pickup, dropoff, stops = [], driverPosition, routeFrom, routeTo, selection, trail, nearbyVehicles, searchRadius, onPick, onRouteMetrics, className }: Props) {
  const points: MapPoint[] = [pickup, ...stops, dropoff, driverPosition, selection?.point].filter((p): p is MapPoint => !!p)
  const pointsKey = JSON.stringify(points)
  const routeStart = routeFrom ?? pickup
  const routeEnd = routeTo ?? dropoff
  const animatedDriverPosition = useAnimatedPosition(driverPosition ?? null)
  const driverHeading = useHeading(driverPosition ?? null)

  return (
    <div className={`${className || ''} at-map-dark at-map-surface z-0`}>
      <GoogleMapView
        points={points}
        pickup={pickup}
        dropoff={dropoff}
        stops={stops}
        driverPosition={animatedDriverPosition}
        driverHeading={driverHeading}
        routeStart={routeStart}
        routeEnd={routeEnd}
        selection={selection}
        trail={trail}
        nearbyVehicles={nearbyVehicles}
        searchRadius={searchRadius}
        onPick={onPick}
        onRouteMetrics={onRouteMetrics}
        pointsKey={pointsKey}
      />
    </div>
  )
}

interface GoogleMapViewProps {
  points: MapPoint[]
  pickup?: MapPoint | null
  dropoff?: MapPoint | null
  stops: (MapPoint & { stopId?: number; visitedAt?: string | null })[]
  driverPosition: MapPoint | null
  driverHeading: number
  routeStart?: MapPoint | null
  routeEnd?: MapPoint | null
  selection?: { point: MapPoint; color: string; glyph: string } | null
  trail?: MapPoint[]
  nearbyVehicles?: { driverId: number; lat: number; lng: number; vehicleIcon: string }[]
  searchRadius?: { center: MapPoint; radiusKm: number; exhausted?: boolean } | null
  onPick?: (point: MapPoint) => void
  onRouteMetrics?: (metrics: RouteMetrics | null) => void
  pointsKey: string
}

const SEARCH_ACTIVE_COLOR = '#00D46A'
const SEARCH_EXHAUSTED_COLOR = '#FF9F43'
const SEARCH_PULSE_CYCLE_MS = 2600
const SEARCH_PULSE_RING_COUNT = 2

setOptions({ key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '', v: 'weekly' })

function googleMarkerIcon(color: string, glyph: string, size = 30): google.maps.Icon {
  const iconPath = glyph === 'P' ? '<path d="M8 10h8l-1 8H9zM9 10l1-3h4l1 3M11 18v2M15 18v2"/>'
    : glyph === 'D' ? '<path d="M5 7h11l3 4v7H5zM8 18a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm8 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"/>'
    : glyph === 'S' ? '<path d="M12 3l2.4 5 5.6.8-4 3.9.9 5.5-4.9-2.6-4.9 2.6.9-5.5-4-3.9 5.6-.8z"/>'
    : '<path d="M5 17h14V7H5zM5 11h14M8 17v2M16 17v2"/>'
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="${color}" stroke="white" stroke-width="2"/><g fill="none" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">${iconPath}</g></svg>`
  return { url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`, scaledSize: new google.maps.Size(size, size), anchor: new google.maps.Point(size / 2, size / 2) }
}

function driverMarkerIcon(heading: number): google.maps.Symbol {
  return {
    path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
    scale: 7,
    fillColor: '#1687FF',
    fillOpacity: 1,
    strokeColor: '#FFFFFF',
    strokeWeight: 2,
    rotation: heading,
    anchor: new google.maps.Point(0, 0),
  }
}

function GoogleMapView({ points, pickup, dropoff, stops, driverPosition, driverHeading, routeStart, routeEnd, selection, trail, nearbyVehicles, searchRadius, onPick, onRouteMetrics, pointsKey }: GoogleMapViewProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<google.maps.Map | null>(null)
  const markersRef = useRef<google.maps.Marker[]>([])
  const linesRef = useRef<google.maps.Polyline[]>([])
  const routeLinesRef = useRef<google.maps.Polyline[]>([])
  const lastFitSignatureRef = useRef('')
  const lastRouteFetchRef = useRef<{ origin: MapPoint; destination: MapPoint; time: number } | null>(null)
  const recenterRafRef = useRef<number | null>(null)
  const [ready, setReady] = useState(false)
  const [error, setError] = useState('')
  const [routePath, setRoutePath] = useState<MapPoint[]>([])

  // --- Search-radius sonar overlay -----------------------------------------
  const searchBoundaryRef = useRef<google.maps.Circle | null>(null)
  const searchPulsesRef = useRef<google.maps.Circle[]>([])
  const searchRadiusMetersRef = useRef<number | null>(null)
  const searchGrowRafRef = useRef<number | null>(null)
  const searchPulseRafRef = useRef<number | null>(null)

  useEffect(() => {
    let cancelled = false
    importLibrary('maps').then(() => {
      if (cancelled || !containerRef.current) return
      mapRef.current = new google.maps.Map(containerRef.current, {
        center: points[0] ?? { lat: 12.9716, lng: 77.5946 },
        zoom: 13,
        disableDefaultUI: true,
        gestureHandling: 'greedy',
        clickableIcons: false,
      })
      new google.maps.TrafficLayer().setMap(mapRef.current)
      if (onPick) mapRef.current.addListener('click', event => {
        if (event.latLng) onPick({ lat: event.latLng.lat(), lng: event.latLng.lng() })
      })
      setReady(true)
    }).catch(() => setError('Google Maps could not be loaded. Check the Maps API key.'))
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !ready) return
    if (!routeStart || !routeEnd) {
      setRoutePath([])
      onRouteMetrics?.(null)
      return
    }

    // The Routes API has a per-project QPS quota — recomputing on every GPS ping burns
    // through it fast and returns 429s. Only recompute when the driver has actually moved
    // a meaningful distance, or enough time has passed.
    const MIN_MOVE_METERS = 40
    const MIN_INTERVAL_MS = 20000
    const last = lastRouteFetchRef.current
    if (last) {
      const elapsed = Date.now() - last.time
      const destinationChanged = last.destination.lat !== routeEnd.lat || last.destination.lng !== routeEnd.lng
      if (!destinationChanged && elapsed < MIN_INTERVAL_MS) return
      if (!destinationChanged && haversineMeters(last.origin, routeStart) < MIN_MOVE_METERS && elapsed < 60000) return
    }

    let cancelled = false
    lastRouteFetchRef.current = { origin: routeStart, destination: routeEnd, time: Date.now() }
    importLibrary('routes').then(({ DirectionsService }) => {
      if (cancelled) return
      const directionsService = new DirectionsService()
      directionsService.route({
        origin: routeStart,
        destination: routeEnd,
        waypoints: (() => {
          const remainingStops = stops.filter(stop => !stop.visitedAt)
          const firstStop = remainingStops[0]
          const endsAtFirstStop = firstStop && firstStop.lat === routeEnd.lat && firstStop.lng === routeEnd.lng
          return endsAtFirstStop ? [] : remainingStops.map(stop => ({ location: stop, stopover: true }))
        })(),
        optimizeWaypoints: false,
        travelMode: google.maps.TravelMode.DRIVING,
        drivingOptions: { departureTime: new Date(), trafficModel: google.maps.TrafficModel.BEST_GUESS },
      }, (result, status) => {
        if (cancelled) return
        if (status !== google.maps.DirectionsStatus.OK || !result?.routes[0]) {
          setRoutePath([])
          onRouteMetrics?.(null)
          return
        }
        const route = result.routes[0]
        const leg = route.legs[0]
        const path = route.overview_path.map(point => ({ lat: point.lat(), lng: point.lng() }))
        onRouteMetrics?.({
          distanceMeters: leg?.distance?.value || 0,
          durationSeconds: leg?.duration?.value || 0,
          updatedAt: Date.now(),
        })
        setRoutePath(path)
      })
    }).catch(() => {
      if (!cancelled) {
        setRoutePath([])
        onRouteMetrics?.(null)
      }
    })
    return () => { cancelled = true }
  }, [onRouteMetrics, ready, routeEnd?.lat, routeEnd?.lng, routeStart?.lat, routeStart?.lng, stops])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !ready) return
    markersRef.current.forEach(marker => marker.setMap(null))
    linesRef.current.forEach(line => line.setMap(null))
    markersRef.current = []
    linesRef.current = []
    const add = (point: MapPoint, color: string, glyph: string, title: string, size?: number) => {
      markersRef.current.push(new google.maps.Marker({ map, position: point, title, icon: googleMarkerIcon(color, glyph, size) }))
    }
    if (pickup) add(pickup, '#00D46A', 'P', 'Pickup')
    stops.forEach(stop => add(stop, '#FFB340', 'S', 'Stop'))
    if (dropoff) add(dropoff, '#FF453A', 'D', 'Delivery')
    if (selection) add(selection.point, selection.color, selection.glyph, 'Selected location')
    nearbyVehicles?.forEach(vehicle => add(vehicle, '#FFFFFF', vehicle.vehicleIcon, 'Nearby vehicle', 24))
    if (driverPosition) {
      markersRef.current.push(new google.maps.Marker({
        map,
        position: driverPosition,
        title: 'Driver location',
        icon: driverMarkerIcon(driverHeading),
      }))
    }
    if (trail && trail.length > 1) linesRef.current.push(new google.maps.Polyline({ map, path: trail, strokeColor: '#00D46A', strokeOpacity: 0.55, strokeWeight: 4, zIndex: 3 }))
    // Radius included so a tier expansion (2km -> 3km -> 8km) re-fits the
    // view wide enough to actually show the new boundary, not just re-center
    // on the same pickup/dropoff points as before.
    const fitSignature = JSON.stringify({ pickup, stops, dropoff, selection, routeEnd, searchRadius })
    if (lastFitSignatureRef.current !== fitSignature) {
      lastFitSignatureRef.current = fitSignature
      if (points.length === 1 && !searchRadius) map.panTo(points[0])
      if (points.length > 1 || searchRadius) {
        const bounds = new google.maps.LatLngBounds()
        points.forEach(point => bounds.extend(point))
        if (searchRadius) {
          // getBounds() is pure geometry (center + radius) — works fine on a
          // Circle that's never actually attached to the map.
          const circleBounds = new google.maps.Circle({ center: searchRadius.center, radius: searchRadius.radiusKm * 1000 }).getBounds()
          if (circleBounds) bounds.union(circleBounds)
        }
        map.fitBounds(bounds, 40)
      }
    }
  }, [dropoff, driverHeading, driverPosition, nearbyVehicles, pickup, pointsKey, ready, routeEnd, searchRadius, selection, stops, trail])

  // Solid boundary ring at the current search radius, smoothly growing
  // whenever the tier widens rather than snapping — the size change IS the
  // signal that the search is actively expanding, no separate copy needed.
    useEffect(() => {
      const map = mapRef.current
      if (!map || !ready) return

      if (!searchRadius) {
        searchBoundaryRef.current?.setMap(null)
        searchBoundaryRef.current = null
        searchPulsesRef.current.forEach(p => p.setMap(null))
        searchPulsesRef.current = []
        searchRadiusMetersRef.current = null
        if (searchGrowRafRef.current) cancelAnimationFrame(searchGrowRafRef.current)
        if (searchPulseRafRef.current) cancelAnimationFrame(searchPulseRafRef.current)
        return
      }

      const color = searchRadius.exhausted ? SEARCH_EXHAUSTED_COLOR : SEARCH_ACTIVE_COLOR
      if (!searchBoundaryRef.current) {
        searchBoundaryRef.current = new google.maps.Circle({
          map,
          clickable: false,
          center: searchRadius.center,
          radius: 0,
          strokeColor: color,
          strokeOpacity: 0.9,
          strokeWeight: 2,
          fillColor: color,
          fillOpacity: 0.06,
          zIndex: 1,
        })
      } else {
        searchBoundaryRef.current.setOptions({ strokeColor: color, fillColor: color, strokeOpacity: searchRadius.exhausted ? 0.7 : 0.9 })
        searchBoundaryRef.current.setCenter(searchRadius.center)
      }

      if (searchPulsesRef.current.length === 0) {
        searchPulsesRef.current = Array.from({ length: SEARCH_PULSE_RING_COUNT }).map(() => new google.maps.Circle({
          map,
          clickable: false,
          center: searchRadius.center,
          radius: 0,
          strokeColor: SEARCH_ACTIVE_COLOR,
          strokeOpacity: 0,
          strokeWeight: 1.5,
          fillColor: SEARCH_ACTIVE_COLOR,
          fillOpacity: 0,
          zIndex: 0,
        }))
      } else {
        searchPulsesRef.current.forEach(p => p.setCenter(searchRadius.center))
      }

      // Grow (or shrink) smoothly from wherever the ring currently is to the
      // new tier's radius, instead of jumping straight to it.
      const targetMeters = searchRadius.radiusKm * 1000
      const fromMeters = searchRadiusMetersRef.current ?? targetMeters
      if (searchGrowRafRef.current) cancelAnimationFrame(searchGrowRafRef.current)
      const growStart = performance.now()
      const growDuration = 700
      const tickGrow = (now: number) => {
        const t = Math.min(1, (now - growStart) / growDuration)
        const eased = 1 - Math.pow(1 - t, 3)
        const current = fromMeters + (targetMeters - fromMeters) * eased
        searchBoundaryRef.current?.setRadius(current)
        searchRadiusMetersRef.current = current
        if (t < 1) searchGrowRafRef.current = requestAnimationFrame(tickGrow)
      }
      searchGrowRafRef.current = requestAnimationFrame(tickGrow)

      // Continuous outward sonar pulse while still actively searching — stops
      // (rings fade to invisible) the moment the widest tier is exhausted.
      if (searchPulseRafRef.current) cancelAnimationFrame(searchPulseRafRef.current)
      if (searchRadius.exhausted) {
        searchPulsesRef.current.forEach(p => p.setOptions({ strokeOpacity: 0, fillOpacity: 0 }))
    } else {
      const pulseStart = performance.now()
      const tickPulse = (now: number) => {
        searchPulsesRef.current.forEach((p, i) => {
          const phase = (((now - pulseStart) / SEARCH_PULSE_CYCLE_MS) + i / searchPulsesRef.current.length) % 1
          p.setRadius(targetMeters * phase)
          const fade = 1 - phase
          p.setOptions({ strokeOpacity: 0.5 * fade, fillOpacity: 0.08 * fade })
        })
        searchPulseRafRef.current = requestAnimationFrame(tickPulse)
      }
      searchPulseRafRef.current = requestAnimationFrame(tickPulse)
    }

    return () => {
      if (searchGrowRafRef.current) cancelAnimationFrame(searchGrowRafRef.current)
      if (searchPulseRafRef.current) cancelAnimationFrame(searchPulseRafRef.current)
    }
  }, [ready, searchRadius?.center.lat, searchRadius?.center.lng, searchRadius?.radiusKm, searchRadius?.exhausted])

  // Full teardown only on unmount — the effect above intentionally keeps
  // reusing the same Circle instances across radius/tier changes.
  useEffect(() => {
    return () => {
      searchBoundaryRef.current?.setMap(null)
      searchPulsesRef.current.forEach(p => p.setMap(null))
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !ready) return
    routeLinesRef.current.forEach(line => line.setMap(null))
    routeLinesRef.current = []
    if (routePath.length < 2) return  
    routeLinesRef.current.push(new google.maps.Polyline({ map, path: routePath, strokeColor: '#061B36', strokeOpacity: 0.7, strokeWeight: 13, zIndex: 3 }))
    routeLinesRef.current.push(new google.maps.Polyline({ map, path: routePath, strokeColor: '#155EEF', strokeOpacity: 0.98, strokeWeight: 7, zIndex: 4 }))
  }, [ready, routePath])

  // Cleanup any in-flight recenter animation on unmount.
  useEffect(() => () => { if (recenterRafRef.current) cancelAnimationFrame(recenterRafRef.current) }, [])

  return (
    <>
      <div ref={containerRef} className="h-full w-full bg-[#e8e8e8]" />
      {error && <div className="absolute inset-0 flex items-center justify-center bg-black/30 px-6 text-center text-xs text-white">{error}</div>}
      {ready && driverPosition && <button
        type="button"
        aria-label="Re-center on driver"
        title="Re-center on driver"
        onClick={() => {
          const map = mapRef.current
          if (!map) return
          // Cancel any animation already in flight so repeated clicks don't
          // stack competing zoom tweens fighting over the map's zoom level.
          if (recenterRafRef.current) cancelAnimationFrame(recenterRafRef.current)
          map.panTo(driverPosition)
          const startZoom = map.getZoom() ?? 13
          const targetZoom = 18
          const startTime = performance.now()
          const duration = 520
          const animate = (now: number) => {
            const progress = Math.min(1, (now - startTime) / duration)
            const eased = 1 - Math.pow(1 - progress, 3)
            map.setZoom(startZoom + (targetZoom - startZoom) * eased)
            if (progress < 1) recenterRafRef.current = requestAnimationFrame(animate)
          }
          recenterRafRef.current = requestAnimationFrame(animate)
        }}
        className="absolute z-20 flex items-center justify-center rounded-[14px] border border-[#00D46A80] bg-[#0A2E1E] text-[#00D46A] shadow-lg"
        style={{ right: 16, bottom: 150, width: 42, height: 42, pointerEvents: 'auto' }}
      >
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <circle cx="12" cy="12" r="3" />
          <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
        </svg>
      </button>}
    </>
  )
}